import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { DashboardLayout } from "../components/layout/DashboardLayout";
import { LoadingState } from "../components/common/LoadingState";
import { ErrorState } from "../components/common/ErrorState";
import { PageHeader } from "../components/common/PageHeader";
import { useDataFetching } from "../hooks/useDataFetching";
import { spacesAPI } from "../api";
import { QUERY_KEYS } from "../utils/constants";
import { formatDateTime } from "../utils/formatters";
import {
  ArrowLeft, Edit, Trash2, Users, Calendar, Building2,
  UserPlus, UserMinus, CheckCircle, XCircle, Clock,
  Shield, Image as ImageIcon
} from "lucide-react";
import { Space, SpaceMember, SpaceRequest, SpaceEvent, User } from "../types";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { DataTable } from "../components/common/DataTable";

export function SpaceDetailsPage() {
  const { spaceId } = useParams<{ spaceId: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("overview");

  const {
    data: spaceData,
    isLoading: isLoadingSpace,
    error: spaceError,
    refetch: refetchSpace
  } = useDataFetching([...QUERY_KEYS.SPACES, spaceId || ''], {
    queryFn: () => spacesAPI.getSpace(spaceId!),
    enabled: !!spaceId,
  });

  const {
    data: membersData,
    isLoading: isLoadingMembers,
    refetch: refetchMembers
  } = useDataFetching([...QUERY_KEYS.SPACE_MEMBERS, spaceId || ''], {
    queryFn: () => spacesAPI.getMembers(spaceId!, 1, 100),
    enabled: !!spaceId && activeTab === "members",
  });

  const {
    data: requestsData,
    isLoading: isLoadingRequests,
    refetch: refetchRequests
  } = useDataFetching([...QUERY_KEYS.SPACE_REQUESTS, spaceId || ''], {
    queryFn: () => spacesAPI.getRequests(spaceId!),
    enabled: !!spaceId && activeTab === "requests",
  });

  const {
    data: eventsData,
    isLoading: isLoadingEvents,
  } = useDataFetching([...QUERY_KEYS.EVENTS, 'space', spaceId || ''], {
    queryFn: () => spacesAPI.getSpaceEvents(spaceId!, 1, 100),
    enabled: !!spaceId && activeTab === "events",
  });

  const space = spaceData as Space;
  // API returns different response structures
  const members = (membersData?.items || []) as SpaceMember[];
  const requests = (requestsData?.items || []) as SpaceRequest[];
  const events = (eventsData?.data?.events || []) as SpaceEvent[];

  const handleDelete = async () => {
    if (confirm("Are you sure you want to delete this space?")) {
      try {
        await spacesAPI.deleteSpace(spaceId!);
        navigate('/spaces');
      } catch (error) {
        console.error("Failed to delete space:", error);
      }
    }
  };

  const handleApproveRequest = async (userId: string) => {
    try {
      await spacesAPI.approveRequest(spaceId!, userId);
      refetchRequests();
      refetchMembers();
      refetchSpace();
    } catch (error) {
      console.error("Failed to approve request:", error);
    }
  };

  const handleRejectRequest = async (userId: string) => {
    try {
      await spacesAPI.rejectRequest(spaceId!, userId);
      refetchRequests();
    } catch (error) {
      console.error("Failed to reject request:", error);
    }
  };

  const handleRemoveMember = async (userId: string) => {
    if (confirm("Are you sure you want to remove this member?")) {
      try {
        await spacesAPI.removeMember(spaceId!, userId);
        refetchMembers();
        refetchSpace();
      } catch (error) {
        console.error("Failed to remove member:", error);
      }
    }
  };

  const memberColumns = [
    {
      key: 'user',
      header: 'Member',
      render: (member: SpaceMember) => {
        const user = (member.userId || member.user_id) as User;
        return (
          <div className="flex items-center gap-3">
            <Avatar className="h-8 w-8">
              <AvatarImage src={user?.avatar_url} />
              <AvatarFallback>
                {user?.first_name?.[0]}{user?.last_name?.[0]}
              </AvatarFallback>
            </Avatar>
            <div>
              <div className="font-medium">
                {user?.first_name} {user?.last_name}
              </div>
              <div className="text-sm text-gray-500">{user?.email}</div>
            </div>
          </div>
        );
      },
    },
    {
      key: 'role',
      header: 'Role',
      render: (member: SpaceMember) => (
        <Badge variant={member.role === 'owner' ? 'default' : 'secondary'}>
          {member.role === 'owner' && <Shield className="h-3 w-3 mr-1" />}
          {member.role || 'Member'}
        </Badge>
      ),
    },
    {
      key: 'joined',
      header: 'Joined',
      render: (member: SpaceMember) => (
        <span className="text-sm text-gray-500">
          {formatDateTime(member.joined_at || member.createdAt || new Date().toISOString())}
        </span>
      ),
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (member: SpaceMember) => {
        if (member.role === 'owner') return null;
        return (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleRemoveMember(
              typeof (member.userId || member.user_id) === 'string' 
                ? (member.userId || member.user_id) as string 
                : ((member.userId || member.user_id) as User)._id
            )}
          >
            <UserMinus className="h-4 w-4" />
          </Button>
        );
      },
    },
  ];

  const requestColumns = [
    {
      key: 'user',
      header: 'User',
      render: (request: SpaceRequest) => {
        const user = (request.userId || request.user_id) as User;
        return (
          <div className="flex items-center gap-3">
            <Avatar className="h-8 w-8">
              <AvatarImage src={user?.avatar_url} />
              <AvatarFallback>
                {user?.first_name?.[0]}{user?.last_name?.[0]}
              </AvatarFallback>
            </Avatar>
            <div>
              <div className="font-medium">
                {user?.first_name} {user?.last_name}
              </div>
              <div className="text-sm text-gray-500">{user?.email}</div>
            </div>
          </div>
        );
      },
    },
    {
      key: 'requested',
      header: 'Requested',
      render: (request: SpaceRequest) => (
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <Clock className="h-4 w-4" />
          {request.requested_at ? formatDateTime(request.requested_at) : 'No date'}
        </div>
      ),
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (request: SpaceRequest) => {
        const userId = typeof (request.userId || request.user_id) === 'string' 
          ? (request.userId || request.user_id) as string 
          : ((request.userId || request.user_id) as User)._id;
        return (
          <div className="flex gap-2">
            <Button
              variant="default"
              size="sm"
              onClick={() => handleApproveRequest(userId)}
            >
              <CheckCircle className="h-4 w-4 mr-1" />
              Approve
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleRejectRequest(userId)}
            >
              <XCircle className="h-4 w-4 mr-1" />
              Reject
            </Button>
          </div>
        );
      },
    },
  ];

  const eventColumns = [
    {
      key: 'name',
      header: 'Event',
      render: (event: SpaceEvent) => (
        <div>
          <div className="font-medium">{event.name}</div>
          <div className="text-sm text-gray-500">{event.description}</div>
        </div>
      ),
    },
    {
      key: 'date',
      header: 'Date & Time',
      render: (event: SpaceEvent) => (
        <div className="flex items-center gap-2 text-sm">
          <Calendar className="h-4 w-4 text-gray-400" />
          {formatDateTime(event.date_and_time)}
        </div>
      ),
    },
    {
      key: 'participants',
      header: 'Participants',
      render: (event: SpaceEvent) => (
        <div className="flex items-center gap-2">
          <Users className="h-4 w-4 text-gray-400" />
          <span>{event.participants?.length || 0} / {event.slots}</span>
        </div>
      ),
    },
    {
      key: 'visibility',
      header: 'Visibility',
      render: (event: SpaceEvent) => (
        <Badge variant={event.visibility === 'public' ? 'default' : 'secondary'}>
          {event.visibility}
        </Badge>
      ),
    },
  ];

  if (isLoadingSpace) return <DashboardLayout><LoadingState /></DashboardLayout>;
  if (spaceError || !space) return <DashboardLayout><ErrorState message={spaceError?.message || "Space not found"} /></DashboardLayout>;

  return (
    <DashboardLayout>
      <PageHeader
        title={
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/spaces')}
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div className="flex items-center gap-3">
              {space.logoUrl && (
                <img 
                  src={space.logoUrl} 
                  alt={space.title}
                  className="h-12 w-12 rounded-lg object-cover"
                />
              )}
              <div>
                <h1 className="text-2xl font-bold">{space.title}</h1>
                {space.category && (
                  <p className="text-sm text-gray-500">{typeof space.category === 'string' ? space.category : space.category.name}</p>
                )}
              </div>
            </div>
          </div>
        }
        actions={
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => navigate(`/spaces/${spaceId}/edit`)}
            >
              <Edit className="h-4 w-4 mr-2" />
              Edit
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete
            </Button>
          </div>
        }
      />

      <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-6">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="members">
            Members ({space.membersCount || 0})
          </TabsTrigger>
          <TabsTrigger value="requests">
            Requests ({space.requestsCount || 0})
          </TabsTrigger>
          <TabsTrigger value="events">
            Events ({space.eventIds?.length || 0})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Space Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">About</label>
                  <p className="mt-1">{space.about}</p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-500">Status</label>
                    <div className="mt-1">
                      <Badge variant={space.is_active !== false ? "default" : "secondary"}>
                        {space.is_active !== false ? "Active" : "Inactive"}
                      </Badge>
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Created</label>
                    <p className="mt-1 text-sm">{formatDateTime(space.createdAt)}</p>
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Campus</label>
                  <div className="mt-1 flex items-center gap-2">
                    <Building2 className="h-4 w-4 text-gray-400" />
                    <span>
                      {typeof space.campusId === 'object' 
                        ? space.campusId.campus_name 
                        : space.campusId}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Statistics</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                    <div className="flex items-center gap-2 mb-1">
                      <Users className="h-4 w-4 text-gray-400" />
                      <span className="text-sm text-gray-500">Members</span>
                    </div>
                    <p className="text-2xl font-bold">{space.membersCount || 0}</p>
                  </div>
                  <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                    <div className="flex items-center gap-2 mb-1">
                      <UserPlus className="h-4 w-4 text-gray-400" />
                      <span className="text-sm text-gray-500">Requests</span>
                    </div>
                    <p className="text-2xl font-bold">{space.requestsCount || 0}</p>
                  </div>
                  <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                    <div className="flex items-center gap-2 mb-1">
                      <Calendar className="h-4 w-4 text-gray-400" />
                      <span className="text-sm text-gray-500">Events</span>
                    </div>
                    <p className="text-2xl font-bold">{space.eventIds?.length || 0}</p>
                  </div>
                  <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                    <div className="flex items-center gap-2 mb-1">
                      <ImageIcon className="h-4 w-4 text-gray-400" />
                      <span className="text-sm text-gray-500">Has Logo</span>
                    </div>
                    <p className="text-2xl font-bold">{space.logoUrl ? "Yes" : "No"}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="members" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Space Members</CardTitle>
              <CardDescription>
                Manage members of this space
              </CardDescription>
            </CardHeader>
            <CardContent>
              {isLoadingMembers ? (
                <LoadingState />
              ) : (
                <DataTable
                  data={members}
                  columns={memberColumns}
                  emptyMessage="No members found"
                />
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="requests" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Join Requests</CardTitle>
              <CardDescription>
                Review and manage pending join requests
              </CardDescription>
            </CardHeader>
            <CardContent>
              {isLoadingRequests ? (
                <LoadingState />
              ) : (
                <DataTable
                  data={requests}
                  columns={requestColumns}
                  emptyMessage="No pending requests"
                />
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="events" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Space Events</CardTitle>
              <CardDescription>
                Events organized by this space
              </CardDescription>
            </CardHeader>
            <CardContent>
              {isLoadingEvents ? (
                <LoadingState />
              ) : (
                <DataTable
                  data={events}
                  columns={eventColumns}
                  emptyMessage="No events found"
                />
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </DashboardLayout>
  );
}
